import { initializeApp, getApps, getApp } from "firebase/app";
import { getFirestore, Firestore, getDocs, getDoc, Query, DocumentReference, QuerySnapshot, DocumentSnapshot } from "firebase/firestore";
import { firebaseConfig } from "@/firebase/config";

/**
 * Utility to get a Firestore instance on the server-side.
 */
export function getDb(): Firestore | null {
  try {
    if (!firebaseConfig || !firebaseConfig.projectId) {
      return null;
    }
    
    const app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
    return getFirestore(app);
  } catch (error) {
    return null;
  }
}

/**
 * safeGetDocs - Executes a Firestore query with a mandatory timeout.
 * Prevents ERR_TIMED_OUT from blocking SSR indefinitely.
 */
export async function safeGetDocs(q: Query, timeoutMs: number = 5000): Promise<QuerySnapshot | null> {
  try {
    const result = await Promise.race([
      getDocs(q),
      new Promise<null>((_, reject) => 
        setTimeout(() => reject(new Error("Firestore operation timed out")), timeoutMs)
      )
    ]);
    return result;
  } catch (error) {
    console.error("Safe-Fetch Error (getDocs):", error);
    return null;
  }
}

/**
 * safeGetDoc - Executes a Document fetch with a mandatory timeout.
 */
export async function safeGetDoc(docRef: DocumentReference, timeoutMs: number = 5000): Promise<DocumentSnapshot | null> {
  try {
    const result = await Promise.race([
      getDoc(docRef),
      new Promise<null>((_, reject) => 
        setTimeout(() => reject(new Error("Firestore operation timed out")), timeoutMs)
      )
    ]);
    return result;
  } catch (error) {
    console.error("Safe-Fetch Error (getDoc):", error);
    return null;
  }
}
